import { extractLettersFields, extractTrustFields } from "@/shared/utils/authorityDocumentFields";
import { hasValidLettersData, hasValidTrustData } from "@/shared/utils/extractionStatus";

import {
  getAuthorityDocumentName,
  getAuthorityDocumentType,
} from "@/shared/constants/legalAuthorityDocuments";

import { WHY_WE_ASK } from "@/shared/constants/whyWeAsk";
import type { AuthorityTaskId } from "@/shared/constants/taskIds";

export type AuthorityVerificationDocumentType =
  | "letters_testamentary"
  | "letters_administration"
  | "trust";

export interface AuthorityVerificationField {
  key: string;
  label: string;
  isDate?: boolean;
  isPerson?: boolean;
}

export interface AuthorityVerificationConfig {
  documentType: AuthorityVerificationDocumentType;
  taskId: AuthorityTaskId;
  documentName: string;
  documentDescription: string;
  uploadStepLabel: string;
  uploadTitle: string;
  uploadDescription: string;
  uploadWhyWeAsk: string;
  confirmTitle: string;
  confirmDescription: string;
  processingTitle: string;
  processingChecklist: readonly string[];
  fields: AuthorityVerificationField[];
  presetFields?: Record<string, string>;
  extractFields: (specific: Record<string, unknown> | null) => Record<string, string>;
  hasValidData: (specific: Record<string, unknown> | null | undefined) => boolean;
}

const LETTERS_FIELDS: AuthorityVerificationField[] = [
  { key: "fiduciaryTitle", label: "Your role" },
  { key: "dateFiled", label: "Date filed", isDate: true },
  { key: "dateIssued", label: "Date of appointment", isDate: true },
  { key: "courtName", label: "Court name" },
  { key: "courtAddress", label: "Court address" },
  { key: "caseNumber", label: "Case number" },
];

const TRUST_FIELDS: AuthorityVerificationField[] = [
  { key: "legalRole", label: "Your legal role" },
  { key: "trustName", label: "Trust name" },
  { key: "trustType", label: "Trust" },
  { key: "dateSigned", label: "Date trust signed", isDate: true },
  { key: "actingTrustee", label: "Acting trustee", isPerson: true },
  { key: "successorTrustee", label: "Successor trustee", isPerson: true },
];

const LETTERS_CHECKLIST = [
  "Document received",
  "Reading names, dates, and court details",
  "Matching them to the estate record",
] as const;

const TRUST_CHECKLIST = [
  "Trust received",
  "Reading names, dates, and trustee details",
  "Matching them to the estate record",
] as const;

const TRUST_DOCUMENT_NAME = "trust document";
const TRUSTEE_ROLE_LABEL = "Trustee";

interface AuthorityVerificationParams {
  isTrustee: boolean;
  probateState: string | null | undefined;
  hasWill: string | null | undefined;
  personFirstName: string;
}

function getLettersConfig(
  probateState: string | null | undefined,
  hasWill: string | null | undefined,
  personFirstName: string,
): AuthorityVerificationConfig {
  const documentName = getAuthorityDocumentName(probateState, hasWill);
  const documentType = getAuthorityDocumentType(hasWill);

  return {
    documentType,
    taskId: "lot",
    documentName,
    documentDescription: "the court document naming you",
    uploadStepLabel: "Add your document",
    uploadTitle: `Upload your ${documentName}`,
    uploadDescription: `This is the court paper that gave you legal authority over the estate. The IRS needs it before it will release ${personFirstName}'s records, so adding it starts the search.`,
    uploadWhyWeAsk: WHY_WE_ASK.lettersOfAuthority(personFirstName),
    confirmTitle: `Confirm what the ${documentName} say`,
    confirmDescription: `These details come from the ${documentName}. Check each item and fix anything that's off.`,
    processingTitle: "Checking your document",
    processingChecklist: LETTERS_CHECKLIST,
    fields: LETTERS_FIELDS,
    extractFields: (specific) => extractLettersFields(specific, documentType),
    hasValidData: hasValidLettersData,
  };
}

function getTrustConfig(personFirstName: string): AuthorityVerificationConfig {
  return {
    documentType: "trust",
    taskId: "trust",
    documentName: TRUST_DOCUMENT_NAME,
    documentDescription: "the document naming you as trustee",
    uploadStepLabel: `Add ${personFirstName}'s trust`,
    uploadTitle: `Upload ${personFirstName}'s trust`,
    uploadDescription:
      "This is the document that names you as trustee. Adding it confirms you can act for the trust, so the search can start.",
    uploadWhyWeAsk: WHY_WE_ASK.trust,
    confirmTitle: `Confirm what ${personFirstName}'s trust says`,
    confirmDescription: "Review each item and fix anything that's off.",
    processingTitle: "Checking your trust",
    processingChecklist: TRUST_CHECKLIST,
    fields: TRUST_FIELDS,
    presetFields: { legalRole: TRUSTEE_ROLE_LABEL },
    extractFields: extractTrustFields,
    hasValidData: hasValidTrustData,
  };
}

export function getAuthorityVerificationConfig({
  isTrustee,
  probateState,
  hasWill,
  personFirstName,
}: AuthorityVerificationParams): AuthorityVerificationConfig {
  return isTrustee
    ? getTrustConfig(personFirstName)
    : getLettersConfig(probateState, hasWill, personFirstName);
}
